/* import __COLOCATED_TEMPLATE__ from './pricing-plan-banner.hbs.js'; */
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
import { momentFromDateTimeWithZone } from '@dashboard/junk-drawer/utils/protos/squareup/common/time/converters';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { CurrencyCode } from '@square/dashboard-protos/models/squareup/common/currency_codes';
import { BannerType } from '@square/dashboard-protos/models/squareup/pricingplan/externalhelperservice';
function defaultLinkHandler() {
  // No-op.
}

/**
 * Banner properties are declared here and mapped to the associated BannerType.
 *
 * Support for new banner types should be added here.
 */
const BannerDisplayPropertyMap = {
  [BannerType.FINISH_SETUP_FOR_SUBSCRIPTION_V1]: {
    variant: 'warning',
    contentText: ({
      i18n
    }) => i18n.t('subscriptions.saas.container.payprodSetupBanner.content'),
    linkText: ({
      i18n
    }) => i18n.t('subscriptions.saas.container.payprodSetupBanner.linkText'),
    linkHref: () => '/dashboard',
    linkHandler: defaultLinkHandler
  },
  [BannerType.FINISH_SETUP_FOR_SUBSCRIPTION_V2]: {
    variant: 'warning',
    contentText: ({
      i18n
    }) => i18n.t('pointOfSale.pricingPlanBanner.finishSetupForSubscriptionV2.content'),
    linkText: ({
      i18n
    }) => i18n.t('pointOfSale.pricingPlanBanner.finishSetupForSubscriptionV2.linkText'),
    linkHref: () => '/signup?v=pricing-plan-collection&confirm=false',
    linkHandler: defaultLinkHandler
  },
  [BannerType.FINISH_SETUP_FOR_PAYMENTS]: {
    variant: 'warning',
    contentText: ({
      i18n
    }) => i18n.t('pointOfSale.pricingPlanBanner.finishSetupForPayments.content'),
    linkText: ({
      i18n
    }) => i18n.t('pointOfSale.pricingPlanBanner.finishSetupForPayments.linkText'),
    linkHref: () => '/signup?v=verify-your-identity&confirm=false',
    linkHandler: defaultLinkHandler
  },
  [BannerType.SUBMIT_SHIPPING_AND_BILLING_INFO]: {
    variant: 'warning',
    contentText: ({
      i18n
    }) => i18n.t('pointOfSale.pricingPlanBanner.submitShippingAndBillingInfo.content'),
    linkText: ({
      i18n
    }) => i18n.t('pointOfSale.pricingPlanBanner.submitShippingAndBillingInfo.linkText'),
    linkHref: () => '/signup?v=pricing-plan-collection&confirm=false',
    linkHandler: defaultLinkHandler
  },
  [BannerType.VERIFY_IDENTITY]: {
    variant: 'warning',
    contentText: ({
      i18n
    }) => i18n.t('pointOfSale.pricingPlanBanner.verifyIdentity.content'),
    linkText: ({
      i18n
    }) => i18n.t('pointOfSale.pricingPlanBanner.verifyIdentity.linkText'),
    linkHref: () => '/signup?v=verify-your-identity&confirm=false',
    linkHandler: defaultLinkHandler
  },
  [BannerType.RETURN_HARDWARE]: {
    variant: 'critical',
    contentText: ({
      getBannerResponse,
      formatter,
      i18n,
      localeTime
    }) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const returnByMoment = momentFromDateTimeWithZone(getBannerResponse.returnHardwareData?.returnBy);
      const formattedReturnBy = localeTime.dateTime(returnByMoment, 'longDate');
      const currencyCodeStr =
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      CurrencyCode[getBannerResponse.returnHardwareData.terminationFee.currencyCode];
      const formattedTerminationFee = formatter.currency(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      getBannerResponse.returnHardwareData.terminationFee.amount, currencyCodeStr, {
        maximumFractionDigits: 0
      });
      return i18n.t('pointOfSale.pricingPlanBanner.returnHardwareV2.content', {
        returnDate: formattedReturnBy,
        terminationFee: formattedTerminationFee
      });
    },
    linkText: ({
      i18n
    }) => i18n.t('pointOfSale.pricingPlanBanner.returnHardwareV2.linkText'),
    linkHref: ({
      userInfo
    }) => {
      const countryCode = (userInfo.countryCode || '').toLowerCase();
      const langCode = (userInfo.languageCode || '').toLowerCase().split('-')[0];
      return `/shop/hardware/${countryCode}/${langCode}/return/return`;
    },
    linkHandler: defaultLinkHandler
  },
  [BannerType.PRICING_PLAN_V2_EXPIRED]: {
    variant: 'warning',
    contentText: ({
      getBannerResponse,
      formatter,
      i18n
    }) => {
      let defaultFeeRate = formatter.basisPoints(getBannerResponse?.pricingPlanV2ExpiredData?.defaultCpFeeRate?.cpBasisPoints, {
        minimumFractionDigits: 0
      });
      const cpInterchangeCents = getBannerResponse?.pricingPlanV2ExpiredData?.defaultCpFeeRate?.cpInterchangeCents;
      const hasCpInterchangeCents = cpInterchangeCents && cpInterchangeCents > 0;
      if (hasCpInterchangeCents) {
        const defaultFeeInterchangeCents = formatter.currencyCent(cpInterchangeCents);
        defaultFeeRate += ` + ${defaultFeeInterchangeCents}`;
      }
      return i18n.t('pointOfSale.pricingPlanBanner.pricingPlanV2Expired.content', {
        defaultFeeRate
      });
    },
    linkText: ({
      i18n
    }) => i18n.t('pointOfSale.pricingPlanBanner.verifyIdentity.linkText'),
    linkHref: () => '/signup?v=verify-your-identity&confirm=false',
    linkHandler: defaultLinkHandler
  },
  [BannerType.FINISH_SETUP_FOR_DUAL_MODE_V1]: {
    variant: 'critical',
    contentText: ({
      i18n
    }) => i18n.t('subscriptions.sub2.spos.subscriptionBanner.noPaymentMethodOrIdv.text'),
    linkText: ({
      i18n
    }) => i18n.t('subscriptions.sub2.spos.subscriptionBanner.noPaymentMethodOrIdv.action'),
    linkHandler: component => component.openSetupGuide()
  },
  [BannerType.ADD_PAYMENT_METHOD_FOR_DUAL_MODE_V1]: {
    variant: 'critical',
    contentText: ({
      getBannerResponse,
      i18n
    }) => {
      const targetPlanName = getBannerResponse?.tieredPricingPlanData?.targetSub2PlanName;
      return i18n.t('subscriptions.sub2.spos.subscriptionBanner.noPaymentMethod.contentText', {
        targetPlan: targetPlanName
      });
    },
    linkText: ({
      i18n
    }) => i18n.t('subscriptions.sub2.spos.subscriptionBanner.noPaymentMethod.action'),
    linkHandler: component => component.openPaymentMethodModal()
  },
  [BannerType.FINISH_IDV_FOR_DUAL_MODE_V1]: {
    variant: 'critical',
    contentText: ({
      i18n
    }) => i18n.t('subscriptions.sub2.spos.subscriptionBanner.noIdv.text'),
    linkText: ({
      i18n
    }) => i18n.t('subscriptions.sub2.spos.subscriptionBanner.noIdv.action'),
    linkHandler: component => component.openSetupGuide()
  }
};

/**
 * This component renders a response retrieved from the PricingPlanHelper.GetBanner
 * API.
 *
 * It is currently used for:
 * - PayProd Pricing Experiment V1 and V2
 * - SPOS Dual Mode M0 and M1
 */
let PricingPlanBanner = (_class = class PricingPlanBanner extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "i18n", _descriptor, this);
    _initializerDefineProperty(this, "formatter", _descriptor2, this);
    _initializerDefineProperty(this, "localeTime", _descriptor3, this);
    _initializerDefineProperty(this, "modal", _descriptor4, this);
    _initializerDefineProperty(this, "setupGuide", _descriptor5, this);
    _initializerDefineProperty(this, "userInfo", _descriptor6, this);
  }
  get bannerType() {
    return this.args.getBannerResponse?.bannerType;
  }
  get bannerDisplayProperties() {
    const bannerType = this.args.getBannerResponse?.bannerType;
    if (!bannerType || BannerDisplayPropertyMap[bannerType] === undefined) {
      return undefined;
    }
    return BannerDisplayPropertyMap[bannerType];
  }
  get variant() {
    return this.bannerDisplayProperties?.variant;
  }
  get contentText() {
    return this.bannerDisplayProperties?.contentText({
      getBannerResponse: this.args.getBannerResponse,
      formatter: this.formatter,
      i18n: this.i18n,
      localeTime: this.localeTime
    });
  }
  get linkText() {
    return this.bannerDisplayProperties?.linkText({
      i18n: this.i18n
    });
  }
  get linkHref() {
    return this.bannerDisplayProperties?.linkHref?.({
      userInfo: this.userInfo
    });
  }
  get linkHandler() {
    return this.bannerDisplayProperties?.linkHandler;
  }
  onLinkClick() {
    if (this.linkHandler) {
      this.linkHandler(this);
    }
  }
  openSetupGuide() {
    this.setupGuide.open();
  }
  async openPaymentMethodModal() {
    // eslint-disable-next-line dashboard/require-modal-open-component
    await this.modal.open('subscriptions/update-payment-method-modal', {
      unitToken: this.userInfo.mainUnitToken,
      onBillingSourceChange: this.args.onPaymentMethodUpdated
    });
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "i18n", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "formatter", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "localeTime", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "modal", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "setupGuide", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "userInfo", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "onLinkClick", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onLinkClick"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "openSetupGuide", [action], Object.getOwnPropertyDescriptor(_class.prototype, "openSetupGuide"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "openPaymentMethodModal", [action], Object.getOwnPropertyDescriptor(_class.prototype, "openPaymentMethodModal"), _class.prototype)), _class);
export { PricingPlanBanner as default };